<script setup>
import { onBeforeUnmount, onMounted, ref, watch } from 'vue';

import { useAlert } from '@/src/composables/useAlert';

import { useOrthancStore } from '../store/dicom-web/orthanc-store';
import DragAndDrop from './DragAndDrop.vue';

const showOverlay = ref(false);
const isDraggingOver = ref(false);
const orthancStore = useOrthancStore();
const dragCounter = ref(0);
const alert = useAlert();
const progressId = ref(null);
const fileInput = ref(null);

const resetDragState = () => {
  isDraggingOver.value = false;
  dragCounter.value = 0;
  showOverlay.value = false;
};

// In the component
const openFiles = async (filesIn) => {
  resetDragState();
  progressId.value = alert.progress('Uploading DICOM files...', 0);

  const unwatch = watch(
    () => orthancStore.activeOperations,
    (ops) => {
      const uploadOp = ops.find(op => op.label === 'Uploading DICOM');
      if (!uploadOp) return;
      const percentage = uploadOp.total
        ? Math.round((uploadOp.loaded / uploadOp.total) * 100)
        : 0;
      const current = Math.min(uploadOp.loaded + 1, uploadOp.total);
      alert.progress(
        `Uploading file ${current} of ${uploadOp.total}...`,
        percentage,
        progressId.value,
      );
    },
    { deep: true },
  );

  try {
    await orthancStore.uploadFiles(filesIn);
    alert.hide(progressId.value);
    progressId.value = null;
    alert.success('All files uploaded successfully!');
  }
  catch (e) {
    alert.hide(progressId.value);
    progressId.value = null;
    alert.error(`Upload failed: ${e.message || e}`);
  }
  finally {
    unwatch();
  }
};

function openFileDialog() {
  fileInput.value.click();
}

function handleFileChange(event) {
  const selectedFiles = event.target.files;
  if (selectedFiles.length) openFiles(selectedFiles);
}

const handleDocumentDragEnter = (event) => {
  event.preventDefault();
  if (event.dataTransfer.types.includes('Files')) {
    dragCounter.value++;
    if (dragCounter.value === 1) {
      isDraggingOver.value = true;
    }
  }
};

const handleDocumentDragOver = (event) => {
  event.preventDefault();
};

const handleDocumentDragLeave = (event) => {
  event.preventDefault();
  if (event.dataTransfer.types.includes('Files')) {
    dragCounter.value--;
    if (dragCounter.value === 0) {
      isDraggingOver.value = false;
    }
  }
};

onMounted(() => {
  document.addEventListener('dragenter', handleDocumentDragEnter);
  document.addEventListener('dragleave', handleDocumentDragLeave);
  document.addEventListener('dragover', handleDocumentDragOver);
});

onBeforeUnmount(() => {
  document.removeEventListener('dragenter', handleDocumentDragEnter);
  document.removeEventListener('dragleave', handleDocumentDragLeave);
  document.removeEventListener('dragover', handleDocumentDragOver);
});
</script>

<template>
  <button class="button btn-outline" @click="openFileDialog">
    <i class="fa-solid fa-user-plus default-icon" />
  </button>
  <input
    ref="fileInput"
    type="file"
    style="display: none"
    aria-labelledby="fileInput"
    directory
    multiple
    @change="handleFileChange"
  >
  <DragAndDrop enabled @drop-files="openFiles">
    <div
      class="overlay"
      :class="{
        'show': showOverlay || isDraggingOver,
        'dragging-active': isDraggingOver,
      }"
    >
      <div
        class="flex flex-col items-center justify-center h-full load-files"
        :class="{ visible: showOverlay || isDraggingOver }"
      >
        <i class="fa-solid fa-file-import fa-2xl default-icon" />
        <br>
        <h2>Drop Files Here</h2>
        <h4>Drag-and-drop your data to automatically add a new study.</h4>
        <p>Supported file types: DICOM (.dcm) or ZIP archives (.zip)</p>
      </div>
    </div>
  </DragAndDrop>
</template>

<style scoped>
.overlay {
  position: fixed;
  z-index: 99999;
  top: 55%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 0;
  height: 0;
  padding: 0;
  background-color: var(--md-bg);
  overflow: hidden;
  transition: all 0.3s ease;
  border-radius: 1rem;
}

.overlay.show {
  width: 65vw;
  height: 65vh;
}

.overlay.dragging-active {
  border: 3px dashed var(--md-border);
}

.add-patient-header,
.load-files {
  opacity: 0;
  transition: opacity 0.3s ease;
}

.add-patient-header.visible,
.load-files.visible {
  opacity: 1;
}
</style>
